/**
 * Security Settings Tab Component
 * Manage account security settings
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CheckCircle, UserCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MyProfileCard() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ name: user?.name || "", phone: user?.phone || "" });
  }, [user?.name, user?.phone]);

  const dirty = form.name !== (user?.name || "") || form.phone !== (user?.phone || "");

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/auth/me`, { name: form.name, phone: form.phone });
      updateUser({ name: res.data.name, phone: res.data.phone });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="my-profile" className="p-4 bg-slate-50 rounded-lg" data-testid="my-profile-card">
      <div className="flex items-center gap-2 mb-3">
        <UserCircle className="w-5 h-5 text-slate-500" />
        <div>
          <p className="font-medium text-slate-700">My Profile</p>
          <p className="text-sm text-slate-500">The name shown in the header, on notes and in activity logs</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="profile-name">Display name</Label>
          <Input id="profile-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="profile-name-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="profile-phone">Phone</Label>
          <Input id="profile-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Optional" data-testid="profile-phone-input" />
        </div>
        <Button onClick={save} disabled={!dirty || saving || form.name.trim().length < 2} data-testid="profile-save-btn">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-2">Signed in as {user?.email}</p>
    </div>
  );
}

export function SecuritySettingsTab({
  settings,
  onChange,
  onChangePassword
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="security-settings-tab">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Account &amp; Security</h2>
        <p className="text-sm text-slate-500">Your profile and account security</p>
      </div>

      <div className="space-y-6">
        <MyProfileCard />

        <div className="p-4 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-700">Change Password</p>
              <p className="text-sm text-slate-500">Update your account password</p>
            </div>
            <Button variant="outline" onClick={onChangePassword} data-testid="change-password-btn">
              Change Password
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium text-slate-700">Two-Factor Authentication</p>
            <p className="text-sm text-slate-500">Add extra security to your account</p>
          </div>
          <Switch
            checked={settings.require_2fa || false}
            onCheckedChange={(checked) => 
              onChange({ ...settings, require_2fa: checked })
            }
          />
        </div>

        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-emerald-800">Account Status: Active</p>
            <p className="text-sm text-emerald-700">
              Your account is in good standing. Last login: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
